import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const projects = [
  {
    title: "Meridian",
    category: "Brand Identity",
    year: "2024",
    image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&h=400&fit=crop",
  },
  {
    title: "Aether",
    category: "Web Experience",
    year: "2024",
    image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3579?w=600&h=400&fit=crop",
  },
  {
    title: "Solstice",
    category: "Editorial Design",
    year: "2023",
    image: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=600&h=400&fit=crop",
  },
  {
    title: "Nocturn",
    category: "Art Direction",
    year: "2023",
    image: "https://images.unsplash.com/photo-1550859492-d5da9d8e45f3?w=600&h=400&fit=crop",
  },
  {
    title: "Terraform",
    category: "Digital Product",
    year: "2022",
    image: "https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=600&h=400&fit=crop",
  },
];

function ProjectItem({ project, onHover, onLeave, index }) {
  const rowRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        rowRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: rowRef.current,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        }
      );
    }, rowRef);
    return () => ctx.revert();
  }, []);

  // Magnetic effect
  useEffect(() => {
    const el = titleRef.current;
    const threshold = 80;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < threshold) {
        const pull = 1 - dist / threshold;
        gsap.to(el, {
          x: dx * pull * 0.3,
          y: dy * pull * 0.2,
          duration: 0.3,
          ease: "power3.out",
          willChange: "transform",
        });
      } else {
        gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: "power3.out" });
      }
    };

    const onLeaveEl = () => {
      gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: "power3.out" });
    };

    window.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeaveEl);
    return () => {
      window.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeaveEl);
    };
  }, []);

  return (
    <div
      ref={rowRef}
      className="group border-b border-ink/10 opacity-0"
      onMouseEnter={(e) => onHover(e, project)}
      onMouseLeave={onLeave}
    >
      <a
        ref={titleRef}
        href="#"
        data-cursor-hover
        className="flex items-baseline justify-between py-6 md:py-8 px-2 md:px-4 no-underline text-ink transition-colors duration-300"
        onClick={(e) => e.preventDefault()}
      >
        <div className="flex items-baseline gap-4 md:gap-8">
          <span className="font-mono text-xs text-ink/30">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold will-change-transform">
            {project.title}
          </span>
        </div>
        <div className="hidden md:flex items-baseline gap-6">
          <span className="font-sans text-sm text-ink/50">{project.category}</span>
          <span className="font-mono text-xs text-ink/30">{project.year}</span>
        </div>
      </a>

      {/* Mobile: inline image */}
      <div className="md:hidden overflow-hidden max-h-0 group-hover:max-h-60 transition-all duration-500">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-48 object-cover grayscale-[30%] contrast-[1.1]"
          loading="lazy"
        />
      </div>
    </div>
  );
}

export default function ProjectGallery() {
  const sectionRef = useRef(null);
  const floatingRef = useRef(null);
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    if (!floatingRef.current) return;

    const moveX = gsap.quickTo(floatingRef.current, "left", { duration: 0.4, ease: "power3.out" });
    const moveY = gsap.quickTo(floatingRef.current, "top", { duration: 0.4, ease: "power3.out" });

    const onMove = (e) => {
      moveX(e.clientX);
      moveY(e.clientY);
    };

    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const handleHover = (e, project) => {
    setActiveProject(project);
    gsap.to(floatingRef.current, { opacity: 1, scale: 1, duration: 0.4, ease: "power3.out" });
  };

  const handleLeave = () => {
    gsap.to(floatingRef.current, {
      opacity: 0,
      scale: 0.9,
      duration: 0.3,
      ease: "power3.out",
      onComplete: () => setActiveProject(null),
    });
  };

  // Section heading reveal
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-gallery-heading]",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="projects" className="px-6 py-16 md:px-12 md:py-24">
      <div data-gallery-heading className="mb-12 md:mb-16">
        <span className="font-mono text-xs tracking-widest uppercase text-ink/40 block mb-3">
          Selected Work
        </span>
        <h2 className="font-serif text-4xl md:text-6xl font-bold">Projects</h2>
      </div>

      <div className="border-t border-ink/10">
        {projects.map((project, i) => (
          <ProjectItem
            key={project.title}
            project={project}
            index={i}
            onHover={handleHover}
            onLeave={handleLeave}
          />
        ))}
      </div>

      {/* Floating preview image — desktop only */}
      <div
        ref={floatingRef}
        className="hidden md:block fixed pointer-events-none z-50 opacity-0 will-change-transform"
        style={{
          transform: "translate(-50%, -50%) scale(0.9)",
          top: 0,
          left: 0,
        }}
      >
        {activeProject && (
          <img
            src={activeProject.image}
            alt={activeProject.title}
            className="w-[320px] h-[220px] object-cover grayscale-[30%] contrast-[1.1]"
            style={{ filter: "grayscale(30%) contrast(1.1)" }}
          />
        )}
      </div>
    </section>
  );
}
